import { FC, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { CollectionInterface } from "../../../../interfaces/collection";
import {
  Button,
  DialogActions,
  FormHelperText,
  InputAdornment,
  InputBase,
} from "@mui/material";
import { useMetaMask } from "metamask-react";
import {
  convertETHtoWei,
  getUserBalanceETH,
} from "../../../../utils/web3Utils";
import CustomDurationPicker from "../../../duration/duration";
import dayjs, { Dayjs } from "dayjs";
import "./makeCollectionOfferDialog.scss";
import { Offer } from "../../../../interfaces/OfferTypes";
import { addressesByNetwork } from "../../../../constants/addresses";
import { encodeParams } from "../../../../utils/signMessagesUtil";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
    background: "rgba(20, 13, 71, 1)",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
    background: "rgba(20, 13, 71, 1)",
  },
  "&.  .MuiDialogTitle-root": {
    background: "rgba(20, 13, 71, 1)",
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} className="dialog-title" {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

const MakeCollectionOfferDialog: FC<MakeCollectionOfferProps> = (
  props: MakeCollectionOfferProps
) => {
  const {
    setShowMakeOfferDialog,
    open,
    collection,
    makeCollectionOffer,
    nonce,
    setShowSwapEthWethDialog,
    userBalanceWETH,
    userBalanceETH,
  } = props;
  const [wethPrice, setWethPrice] = useState<number>(0);
  const { account, chainId } = useMetaMask();
  const [userBalance, setUserBalance] = useState<string>("");
  const [date, setDate] = useState<Dayjs>(dayjs().add(1, "day"));
  const [error, setError] = useState(false);
  const [disableButton, setDisableButton] = useState<boolean>(true);

  useEffect(() => {
    if (!account) return;

    getUserBalanceETH(account).then((data: string) => {
      setUserBalance(data.slice(0, 5));
    });
  }, []);

  const handleInputChange = (event: any) => {
    const newValue = Number(event.target.value);

    if (0 < newValue && newValue <= Number(userBalanceWETH)) {
      setError(false);
      setDisableButton(false);
    } else {
      setError(true);
      setDisableButton(true);
    }
    setWethPrice(event.target.value);
  };

  const handleClose = () => {
    setShowMakeOfferDialog(false);
  };

  const handleOnMakeCollectionOffer = async (
    buyer: string | null,
    collectionId: string,
    price: string,
    endTime: number
  ) => {
    if ((price && price <= "0") || !buyer || !chainId) {
      return;
    }
    const date = new Date(endTime);
    const utcDate = new Date(date.toUTCString());
    const collectionOffer: Offer = {
      buyer: buyer,
      collection: collectionId,
      tokenId: 0,
      price: price,
      amount: 1,
      strategy:
        addressesByNetwork(chainId)
          .STRATEGY_STANDARD_SALE_SINGLE_ITEM_IN_COLLECTION,
      currency: addressesByNetwork(chainId).WETH,
      nonce: nonce,
      startTime: Math.round(new Date().getTime() / 1000),
      endTime: Math.round(utcDate.getTime() / 1000),
      params: encodeParams([]),
      signature: "",
    };
    await makeCollectionOffer(collectionOffer);
    handleClose();
  };

  return (
    <div>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <BootstrapDialogTitle
          id="customized-dialog-title"
          onClose={handleClose}
        >
          Make Collection Offer
        </BootstrapDialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center ",
          }}
        >
          <div className="offer-container">
            <div className="offer-name-details-wrapper">
              <div className="offer-nft-name-icon-wrapper">
                <div className="offer-nft-icon">
                  <img
                    src={
                      collection.image_url
                        ? collection.image_url
                        : "/noContent.svg"
                    }
                    width="100"
                    height="100"
                  />
                </div>
                <div className="nft-name-collection-wrapper">
                  <div className="offer-nft-name">
                    {collection.name} collection
                  </div>
                </div>
              </div>
            </div>
            <div className="offer-set-price">
              <div className="duration-const">Set Price</div>
              <div className="gray-small-font">
                WETH Balance: {userBalanceWETH}
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "1rem",
                }}
              >
                <div className="offer-price">
                  <InputBase
                    sx={{
                      width: "100%",
                      fontFamily: "LemonMilk",
                      marginLeft: "1rem",
                    }}
                    placeholder="Price"
                    value={wethPrice}
                    error={error}
                    onChange={handleInputChange}
                    endAdornment={
                      <InputAdornment
                        sx={{ color: "white", marginRight: "1.5rem" }}
                        position="end"
                      >
                        <div>WETH</div>
                      </InputAdornment>
                    }
                  />
                </div>

                <Button
                  variant="text"
                  className="swap-eth-weth"
                  onClick={() => setShowSwapEthWethDialog(true)}
                >
                  <div className="swap-text">Swap ETH/WETH</div>
                </Button>
              </div>
              {error && (
                <FormHelperText error>
                  The amount cannot be higher than the wallet balance.
                </FormHelperText>
              )}
            </div>

            <div className="offer-duration">
              <div className="duration-const">Set Duration</div>
              <div className="duration-value">
                <CustomDurationPicker setDate={setDate} date={date} />
              </div>
            </div>
          </div>
        </DialogContent>

        <DialogActions>
          <Button
            className={disableButton ? "gradient-btn-disabled" : "gradient-btn"}
            autoFocus
            disabled={disableButton}
            onClick={async () =>
              await handleOnMakeCollectionOffer(
                account,
                collection.collection_id,
                convertETHtoWei(wethPrice.toString()),
                date.valueOf()
              )
            }
          >
            Make Collection Offer
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </div>
  );
};

interface MakeCollectionOfferProps {
  setShowMakeOfferDialog: Function;
  makeCollectionOffer: Function;
  setShowSwapEthWethDialog: Function;
  open: boolean;
  collection: CollectionInterface;
  nonce: number;
  userBalanceWETH: string;
  userBalanceETH: string;
}

export default MakeCollectionOfferDialog;
